"use client";

import { MinusIcon, PlayIcon } from "@heroicons/react/24/solid";
import { TrashIcon } from "@heroicons/react/24/solid";
import Image from "next/image";
import { useState } from "react";
import YouTube from "react-youtube";

import { NormalText } from "@/components/text/NormalText";
import { SoftText } from "@/components/text/SoftText";
import { AudioInputState, Cover } from "@/types/types";
import { trackLengthFormat } from "@/utils/formatTime/trackLengthFormat";

const opts: Record<string, unknown> = {
  height: "100%",
  width: "100%",
  playerVars: {
    // https://developers.google.com/youtube/player_parameters
    autoplay: 1,
    rel: 0,
  },
};

export function CoverPresentation({
  selectedCover,
  clearInput,
  hideClearButton,
}: {
  selectedCover: Cover;
  clearInput: AudioInputState["clearInput"];
  hideClearButton: boolean;
}) {
  const [coverOpen, setCoverOpen] = useState(false);

  return (
    <div className="rounded-3xl bg-white px-4 py-2 shadow-sm ring-1 ring-gray-900/5">
      <div className="flex w-full items-stretch">
        <button
          onClick={() => {
            if (coverOpen) {
              setCoverOpen(false);
            } else {
              setCoverOpen(true);
            }
          }}
          className="pr-2"
          type="button"
        >
          {coverOpen ?
            <MinusIcon className="h-6 w-6 shrink-0 fill-gray-900" />
          : <PlayIcon className="h-6 w-6 shrink-0 fill-gray-900" />}
        </button>
        <div className="flex grow">
          <div className="flex h-full items-center">
            <div className="flex h-12 w-12 overflow-hidden rounded-2xl xl:h-14 xl:w-14">
              <Image
                src={selectedCover.thumbnailUrl}
                alt={selectedCover.title}
                width={100}
                height={100}
                className="-mt-[18.5%] h-[137%] object-cover"
                unoptimized
              />
            </div>
          </div>

          <div className="grow pl-2 xl:pl-4">
            <NormalText
              elementType="p"
              className="mb-1 text-left text-gray-900"
            >
              {selectedCover.title}
            </NormalText>
            <SoftText elementType="p" className="text-left font-normal">
              {trackLengthFormat(selectedCover.audioLength)}
            </SoftText>
          </div>

          {!hideClearButton && (
            <div className="flex h-full items-center pl-2">
              <button onClick={() => clearInput()}>
                <TrashIcon className="h-6 w-6" />
              </button>
            </div>
          )}
        </div>
      </div>
      {coverOpen && (
        <div className="flex justify-center p-5">
          <div className="w-full max-w-sm">
            <div className="aspect-h-9 aspect-w-16 relative">
              <YouTube videoId={selectedCover.id} opts={opts} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
